import { AccountPassword } from "../components/AccountPassword";
import { ImportPanel } from "../components/ImportPanel";
import { copy } from "../copy";
import { getMetricsAdapter } from "../domain/adapters";
import { connectionLine, listedAccounts } from "../domain/accounts";
import { formatWhen } from "../lib/format";
import { useApp } from "../state/store";
import { Executors } from "./Executors";
import { FindForm } from "./Find";
import { Tasks } from "./Tasks";

const SECTIONS = ["executors", "account", "notion", "find"] as const;
type Section = (typeof SECTIONS)[number];

export function Settings() {
  const app = useApp();
  const section = readSection(app.href);
  return (
    <div className="stack">
      <div className="status-tabs" role="tablist">
        {SECTIONS.map((id) => (
          <button key={id} type="button" role="tab" aria-selected={section === id} onClick={() => app.navigate("settings", id === "executors" ? "" : `section=${id}`)}>
            {copy.settingsTabs[id]}
          </button>
        ))}
      </div>
      {section === "executors" ? (
        <>
          <Executors />
          <Tasks />
        </>
      ) : null}
      {section === "account" ? <AccountPanel /> : null}
      {section === "notion" ? <NotionPanel /> : null}
      {section === "find" ? <FindForm intent="prefs" /> : null}
    </div>
  );
}

function readSection(href: string): Section {
  const value = new URLSearchParams(href.split("?")[1] ?? "").get("section");
  return SECTIONS.includes(value as Section) ? (value as Section) : "executors";
}

function NotionPanel() {
  const app = useApp();
  const schema = app.notion.schema;
  const xAdapter = getMetricsAdapter("x");
  return (
    <div className="stack">
      <section className="panel stack">
        <header className="panel-head">
          <h2>Notion</h2>
          <button type="button" className="button primary" onClick={() => void app.syncNow(true)} disabled={app.syncing}>
            {app.syncing ? "同步中" : "立即同步"}
          </button>
        </header>
        <dl className="facts">
          <div>
            <dt>最近成功同步</dt>
            <dd>{formatWhen(app.notion.lastSyncAt)}</dd>
          </div>
          <div>
            <dt>数据源</dt>
            <dd>{schema?.dataSourceId || "同步后显示"}</dd>
          </div>
          <div>
            <dt>状态字段</dt>
            <dd>{schema?.statusType === "status" ? "status" : schema?.statusType === "select" ? "select" : "尚未确认"}</dd>
          </div>
          <div>
            <dt>可选属性</dt>
            <dd>
              {schema
                ? [
                    schema.publishUrl ? "发布链接" : null,
                    schema.plannedDate ? "计划发布日" : null,
                    schema.platforms ? "发布平台" : null,
                    schema.notes ? "备注" : null,
                  ]
                    .filter(Boolean)
                    .join("、") || "都还没有，缺少时相关输入会停用"
                : "同步一次后才知道选题库里有哪些列"}
            </dd>
          </div>
        </dl>
        {schema?.missingRequired.length ? (
          <p className="inline-error">选题库缺少：{schema.missingRequired.join("、")}</p>
        ) : null}
        {app.notion.lastError ? <p className="inline-error">{app.notion.lastError}</p> : <p className="muted">选题以 Notion 为准。这里的缓存大约每 5 分钟刷新，也可以手动同步。</p>}
        <p className="muted">数据库：AI准点下班研究所 · 选题库。Worker 使用 NOTION_TOKEN 访问，页面上看不到令牌。</p>
      </section>
      <section className="panel stack">
        <h2>导入</h2>
        <ImportPanel />
      </section>
      {xAdapter.source === "pull" && !xAdapter.pull ? (
        <p className="muted">X 的公开指标适配器已经留好接口，但还没有实现，所以 X 目前也是手动录入。</p>
      ) : null}
    </div>
  );
}

function AccountPanel() {
  const app = useApp();
  return (
    <div className="stack">
      <section className="panel stack">
        <header className="panel-head">
          <h2>账号</h2>
        </header>
        <p>
          当前登录：<strong>{app.email}</strong>
        </p>
        <p className="muted">只有 ALLOWED_EMAILS 里的邮箱可以进入。退出后本机缓存还在，换一个允许的账号会分开存放。</p>
        <ul className="account-list">
          {listedAccounts(app.state.accounts ?? [], {
            topics: app.state.topics,
            tasks: app.state.tasks ?? [],
            snapshots: app.state.snapshots,
            postMetrics: app.state.postMetrics,
          }).filter((profile) => profile.platform === "小红书").map((profile) => (
            <li key={profile.id}>
              <b>{profile.displayName}</b>
              <span>{profile.platform}</span>
              <small>{connectionLine(profile.platform, app.state.snapshots, app.state.postMetrics)}</small>
            </li>
          ))}
        </ul>
        {app.devPreview ? (
          <p className="muted">本地预览没有 Clerk，不能在这里设置密码。</p>
        ) : (
          <AccountPassword />
        )}
        {app.signOut ? (
          <button type="button" className="button" onClick={app.signOut}>
            退出登录
          </button>
        ) : null}
      </section>
    </div>
  );
}
